import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { v4 as newGuid } from "uuid";
import { SmallInputLabel, UserAutocomplete } from "../../../../components";
import { useSession } from "../../../../hooks";
import { useCreateOneOnOneMeeting, useNewGetUsers, UserDto } from "../../../../http";

interface ICreateOneOnOneMeetingWithLeadFormState {
  lead: UserDto | null;
}

interface IProps {
  open: boolean;
  onClose: () => void;
  onAdded?: () => void;
}

const CreateOneOnOneMeetingWithLeadModal = (props: IProps) => {
  const sessionUser = useSession();
  const { mutate: createMeeting } = useCreateOneOnOneMeeting();
  const { data: usersData } = useNewGetUsers();
  const {
    formState: { errors },
    control,
    handleSubmit,
    reset,
  } = useForm<ICreateOneOnOneMeetingWithLeadFormState>({
    defaultValues: {
      lead: null,
    },
  });

  useEffect(() => {
    if (!usersData) return;

    // Find the session user since the session user object doesn't contain the peopleLeadUserId.
    const sessionUserData = usersData.users.find((u) => u.userId === sessionUser.userId);
    if (!sessionUserData?.peopleLeadUserId) return;

    // Pre-select the lead in the dropdown if the session user has a people lead.
    const lead = usersData.users.find((u) => u.userId === sessionUserData.peopleLeadUserId) ?? null;
    reset({ lead });
  }, [usersData, reset, sessionUser.userId]);

  function handleCreateClicked() {
    handleSubmit((data) => {
      createMeeting(
        {
          meetingGuid: newGuid(),
          leadUser: data.lead!,
          otherUser: {
            displayName: sessionUser.displayName,
            profilePhotoSmallUrl: sessionUser.profilePhotoSmallUrl,
            userId: sessionUser.userId,
            enabled: true,
          },
        },
        {
          onSuccess: () => {
            reset(undefined, { keepDefaultValues: true });
            props.onAdded?.();
            props.onClose();
          },
        },
      );
    })();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onClose();
  }

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <DialogTitle>Create 1:1 Meeting With Lead</DialogTitle>
      <DialogContent>
        <Stack spacing={0.25}>
          <SmallInputLabel>Lead</SmallInputLabel>
          <Controller
            name="lead"
            control={control}
            rules={{ required: "Lead is required" }}
            render={({ field }) => (
              <UserAutocomplete
                placeholder="Select a person..."
                optionsFilter={(u) => u.userId !== sessionUser.userId}
                error={errors["lead"] !== undefined}
                helperText={errors["lead"]?.message}
                onChange={(value) => field.onChange(value)}
                value={field.value}
                disabled
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreateClicked}>
          Create Meeting
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { CreateOneOnOneMeetingWithLeadModal };
